import { v4 as uuidv4 } from 'uuid'
import { CategoryColour } from '../../../../data/gql-gen/questionnaire/graphql'
import {
  QuestionKind,
  QuestionSettingCode,
  QuestionTypeCode,
  SettingInputInput,
  SettingValue
} from '../../../../data/model/questionnaire'
import {
  EntryType,
  Option,
  RawQuestionnaireEntry,
  Row
} from '../../../../hooks/import/models'
import {
  CONJOINT_ATTRIBUTE_PREFIX,
  CONJOINT_LEVEL_PREFIX,
  HIDDEN_MARKETS_PREFIX,
  MARKET_EXPORT_MARKER,
  MAXDIFF_LABEL_NAMES,
  MEDIA_IMAGE_LABEL,
  MEDIA_VIDEO_PREFIX,
  Order,
  QUESTION_TYPE_CONJOINT,
  QUESTION_TYPE_MAXDIFF,
  SCALE_LABEL_NAMES
} from '../../../../utils/surveyDocBuilder/model'
import { colours as TextHighlighterColours } from '../../TextHighlighter/TextHighlighterColourSelect'
const getTextFromParagraph = (paragraph: Element) => {
  const runsXml = paragraph.getElementsByTagName('w:r')
  const texts = Array.from(runsXml).reduce((fullText, r) => {
    const textsXml = r.childNodes
    const textsInRun = Array.from(textsXml).reduce((textInRun, t) => {
      if (t.nodeName === 'w:t') {
        // @todo Legacy eslint violation – fix this when editing
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return textInRun + (t?.textContent ?? '')
      }
      if (t.nodeName === 'w:br' && fullText !== '') {
        return `${textInRun}\n`
      }
      return textInRun
    }, '')
    return fullText + textsInRun
  }, '')
  return texts
}

const checkIfFullTextIsQuestion = (fullText: string) => {
  const audienceQ = /^(A\d+\s)/
  const questionnaireQ = /^(Q\d+\s)/
  const textQ = /^(T\d+\s)/
  if (
    audienceQ.test(fullText) ||
    questionnaireQ.test(fullText) ||
    textQ.test(fullText)
  ) {
    return true
  }
  return false
}

const isNotForImport = (fullText: string) => {
  const sectionStart = /^S\d+\s\(Section\s-\sstart\)/
  const sectionEnd = /^S\d+\s\(Section\s-\send\)/
  const fork = /^Fork\s\d+/
  if (
    sectionStart.test(fullText) ||
    sectionEnd.test(fullText) ||
    fork.test(fullText) ||
    fullText.startsWith('Question Behaviour Summary:') ||
    fullText.startsWith('Questions with display logic:') ||
    fullText.startsWith('Responses with display logic/masking:')
  ) {
    return true
  }

  return false
}

interface QuestionTypeInfo {
  questionType: QuestionTypeCode
  settings?: SettingInputInput
  qNum?: string
}

// Undefined when the header line carries no recognisable prefix — such an
// entry can't be addressed positionally. (This used to return the number 0,
// which `RawQuestionnaireEntry.qNum: string | undefined` quietly mistyped and
// the preview rendered as a literal "0".)
const getQuestionNumber = (text: string): string | undefined => {
  const regEx = /[AQT]\d+/
  const match = text.match(regEx)
  return match ? match[0] : undefined
}

const tryGetQuestionType = (
  text: string,
  includeTranslationOnlyTypes = false
) => {
  const qNum = getQuestionNumber(text)
  if (includeTranslationOnlyTypes) {
    if (text.includes(QUESTION_TYPE_MAXDIFF)) {
      return {
        questionType: QuestionTypeCode.MaxDiff,
        qNum
      } as QuestionTypeInfo
    }
    if (text.includes(QUESTION_TYPE_CONJOINT)) {
      return {
        questionType: QuestionTypeCode.Conjoint,
        qNum
      } as QuestionTypeInfo
    }
  }
  if (text.includes('Single select')) {
    return {
      questionType: QuestionTypeCode.Basic,
      settings: {
        questionSettingCode: QuestionSettingCode.BasicChoice,
        settingValue: 'single_choice'
      },
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Multi select')) {
    return {
      questionType: QuestionTypeCode.Basic,
      settings: {
        questionSettingCode: QuestionSettingCode.BasicChoice,
        settingValue: 'multiple_choice'
      },
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Matrix - single select')) {
    return {
      questionType: QuestionTypeCode.Matrix,
      settings: {
        questionSettingCode: QuestionSettingCode.MatrixChoice,
        settingValue: 'single_choice'
      },
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Matrix - multi select')) {
    return {
      questionType: QuestionTypeCode.Matrix,
      settings: {
        questionSettingCode: QuestionSettingCode.MatrixChoice,
        settingValue: 'multiple_choice'
      },
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Ranked')) {
    return {
      questionType: QuestionTypeCode.Ranked,
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Slider')) {
    return {
      questionType: QuestionTypeCode.Scale,
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Text input')) {
    return {
      questionType: QuestionTypeCode.FreeText,
      settings: {
        questionSettingCode: QuestionSettingCode.ContentStructure,
        settingValue: 'unstructured'
      },
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Text instruction')) {
    return {
      questionType: QuestionTypeCode.TextCard,
      qNum
    } as QuestionTypeInfo
  }
  if (text.includes('Text highlighter - single select')) {
    return {
      questionType: QuestionTypeCode.TextHighlighter,
      settings: {
        questionSettingCode: QuestionSettingCode.BasicChoice,
        settingValue: 'single_choice'
      },
      qNum
    }
  }
  if (text.includes('Text highlighter - multi select')) {
    return {
      questionType: QuestionTypeCode.TextHighlighter,
      settings: {
        questionSettingCode: QuestionSettingCode.BasicChoice,
        settingValue: 'multiple_choice'
      },
      qNum
    }
  }
  return undefined
}

const defaultResponseOptions = [
  "Don't know",
  'Prefer not to say',
  'None of these'
]
const filterOption = (text: string) => {
  const randomiseOrder = Order.Randomise.toString()
  const reverseOrder = Order.Reverse.toString()
  const choiceLimit = /^Choice\slimit:\s\d+$/
  const choiceRange = /^Choice\srange:\s\d+-\d+$/
  return !(
    text === randomiseOrder ||
    text === reverseOrder ||
    choiceLimit.test(text) ||
    choiceRange.test(text) ||
    // Per-item market-visibility annotation the export writes under an option /
    // row (e.g. "Hidden in: 🇩🇪 Germany") — not a response option itself.
    text.trim().startsWith(HIDDEN_MARKETS_PREFIX)
  )
}
/// 1. each item in the array represents a response option
/// 2. remove the number and the dot from the beginning of the text
/// 3. remove the [text entry enabled] marker
/// 4. remove the [pinned] marker
/// 5. remove the [exclusive] marker
const getOptionsFromText = (text: string[]) => {
  return text
    .filter(filterOption)
    .map((t) => {
      const textWithMarkersRemoved = t
        .replace(/^(\d+\.\s)/g, '')
        .replace(/(\s+\[text entry enabled\])/g, '')
        .replace(/(\s+\[pinned\])/g, '')
        .replace(/(\s+\[exclusive\])/g, '')
      return {
        id: uuidv4(),
        value: textWithMarkersRemoved
      } as Option
    })
    .filter((t) => !defaultResponseOptions.includes(t.value || ''))
}

// `RawQuestionnaireEntry.title` is declared non-optional, but indexing a string[]
// types as `string` even when the slot is missing (`noUncheckedIndexedAccess` is
// off). A blank title paragraph — an untranslated field in a per-language export
// — therefore produced `undefined`, which reaches `importQuestionnaireV2` as a
// null for a non-null `title` and fails the whole import.
const titleAt = (text: string[], index: number): string => text.at(index) ?? ''

export const processBasicQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.BasicType,
    questionType: QuestionTypeCode.Basic,
    title: titleAt(text, index),
    options: getOptionsFromText(text.slice(index + 1)),
    kind: questionKind,
    rows: []
  }
  return question
}

export const processMatrixQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const columnsIndex = text.findIndex((t) => t.includes('Columns'))
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.MatrixType,
    questionType: QuestionTypeCode.Matrix,
    title: titleAt(text, index),
    rows: text
      .slice(index + 1, columnsIndex)
      .filter(filterOption)
      .map(
        (t) => ({ id: uuidv4(), value: t.replace(/^(\d+\.\s)/g, '') }) as Row
      ),
    kind: questionKind,
    options: getOptionsFromText(text.slice(columnsIndex + 1))
  }
  return question
}

export const processRankedQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.RankedType,
    questionType: QuestionTypeCode.Ranked,
    title: titleAt(text, index),
    options: getOptionsFromText(text.slice(index + 1)),
    kind: questionKind,
    rows: []
  }
  return question
}

// Read back by name, not ordinal: a label is only printed when it has text, so
// an unlabelled centre would shift every position after it.
const getScaleLabelsFromText = (text: string[]) => {
  const labels: { position: number; value: string }[] = []
  Object.entries(SCALE_LABEL_NAMES).forEach(([position, name]) => {
    if (!name) return
    const prefix = `${name}: `
    const line = text.find((t) => t.trim().startsWith(prefix))
    const value = line?.trim().slice(prefix.length).trim()
    if (value) labels.push({ position: Number(position), value })
  })
  return labels.sort((a, b) => a.position - b.position)
}

export const processScaleQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.ScaleType,
    questionType: QuestionTypeCode.Scale,
    title: titleAt(text, index),
    // Not `options` — that would mint Q<n>.option.<m> keys and collide.
    scaleLabels: getScaleLabelsFromText(text.slice(index + 1)),
    options: [],
    kind: questionKind,
    rows: []
  }
  return question
}

const getFreeTextSettings = (text: string[]) => {
  const settings: SettingInputInput[] = []
  text.forEach((t) => {
    if (t.includes('free text')) {
      settings.push({
        questionSettingCode: QuestionSettingCode.ContentStructure,
        settingValue: 'unstructured'
      })
    }
    if (t.includes('number')) {
      settings.push({
        questionSettingCode: QuestionSettingCode.ContentStructure,
        settingValue: SettingValue.Numeric1
      })
    }
    if (t.includes('postal code')) {
      settings.push({
        questionSettingCode: QuestionSettingCode.ContentStructure,
        settingValue: SettingValue.PostCode
      })
    }
    if (t.includes('email')) {
      settings.push({
        questionSettingCode: QuestionSettingCode.ContentStructure,
        settingValue: SettingValue.Email
      })
    }
  })

  return settings[0]
}

export const processFreeTextQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const setting = getFreeTextSettings(text.slice(index + 1))
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.FreeTextType,
    questionType: QuestionTypeCode.FreeText,
    title: titleAt(text, index),
    options: [],
    kind: questionKind,
    setting,
    rows: []
  }
  return question
}

export const processTextCardQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  // The paragraph after the title is the body — unless the card has none, in
  // which case it is the media line, or nothing at all for a title-only card.
  // `.at()` rather than `[]` because `noUncheckedIndexedAccess` is off: indexing
  // types as `string`, so the nil guard below reads as redundant and the lint
  // autofix strips it. `.at()` is typed `string | undefined` at the signature,
  // which survives both narrowing and the autofix.
  const next = text.at(index + 1)
  const isMedia =
    next?.startsWith(MEDIA_IMAGE_LABEL) || next?.startsWith(MEDIA_VIDEO_PREFIX)
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.TextCardType,
    questionType: QuestionTypeCode.TextCard,
    title: titleAt(text, index),
    body: isMedia ? undefined : next,
    options: [],
    kind: questionKind,
    rows: []
  }
  return question
}

// Reached only via `includeTranslationOnlyTypes`.
export const processMaxDiffQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const rest = text.slice(index + 1)
  const labelAt = (name: string) => {
    const prefix = `${name}: `
    const line = rest.find((t) => t.trim().startsWith(prefix))
    return line?.trim().slice(prefix.length).trim() || undefined
  }
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.MaxDiffType,
    questionType: QuestionTypeCode.MaxDiff,
    title: titleAt(text, index),
    maxDiffLabels: {
      mostLabel: labelAt(MAXDIFF_LABEL_NAMES.mostLabel),
      leastLabel: labelAt(MAXDIFF_LABEL_NAMES.leastLabel),
      attributeLabel: labelAt(MAXDIFF_LABEL_NAMES.attributeLabel)
    },
    // A MaxDiff's items are ordinary response options and translate as such.
    options: getOptionsFromText(
      rest.filter((t) => !isMaxDiffLabelLine(t.trim()))
    ),
    kind: questionKind,
    rows: []
  }
  return question
}

const isMaxDiffLabelLine = (line: string) =>
  Object.values(MAXDIFF_LABEL_NAMES).some((name) =>
    line.startsWith(`${name}: `)
  )

export const processConjointQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  // "Attribute 1: Brand" followed by its indented "Level 1: Acme" lines. The
  // value is matched as possibly-empty: a per-language export writes an
  // untranslated name as "Attribute 1:" with nothing after it, and skipping
  // that line would shift every later attribute onto the wrong field.
  const attributes: { name: string; levels: string[] }[] = []
  text.slice(index + 1).forEach((raw) => {
    const line = raw.trim()
    const attributeMatch = line.match(
      new RegExp(`^${CONJOINT_ATTRIBUTE_PREFIX}\\s+\\d+:\\s*(.*)$`)
    )
    if (attributeMatch) {
      attributes.push({ name: attributeMatch[1].trim(), levels: [] })
      return
    }
    const levelMatch = line.match(
      new RegExp(`^${CONJOINT_LEVEL_PREFIX}\\s+\\d+:\\s*(.*)$`)
    )
    if (levelMatch && attributes.length > 0) {
      attributes[attributes.length - 1].levels.push(levelMatch[1].trim())
    }
  })
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.ConjointType,
    questionType: QuestionTypeCode.Conjoint,
    title: titleAt(text, index),
    attributes,
    options: [],
    kind: questionKind,
    rows: []
  }
  return question
}

export const processTextHighlighterQuestion = (
  text: string[],
  index: number,
  questionKind: QuestionKind
) => {
  const question: RawQuestionnaireEntry = {
    id: uuidv4(),
    entryType: EntryType.TextHighlighterType,
    questionType: QuestionTypeCode.TextHighlighter,
    title: titleAt(text, index),
    body: text[index + 1],
    options: text
      .slice(index + 2)
      .filter((t) => t.match(/^\d+\.\s/))
      .map((t, index) => {
        const textWithoutNumber = t.replace(/^(\d+\.\s)/g, '')
        const colourMatch = textWithoutNumber.match(/^\[([A-Za-z]+)\] /)
        const colour: CategoryColour | undefined = colourMatch
          ? {
              label: colourMatch[1],
              hex:
                TextHighlighterColours.find((c) => c.name === colourMatch[1])
                  ?.value ?? TextHighlighterColours[0].value
            }
          : undefined
        const value = textWithoutNumber.replace(/^\[[A-Za-z]+\] /, '')

        return {
          id: uuidv4(),
          value,
          position: index,
          colour
        } as Option
      }),
    kind: questionKind,
    rows: []
  }
  return question
}

const processByQuestionType = (
  text: string[],
  index: number,
  type: QuestionTypeCode,
  questionKind: QuestionKind
) => {
  switch (type) {
    case QuestionTypeCode.Basic:
      return processBasicQuestion(text, index, questionKind)
    case QuestionTypeCode.Matrix:
      return processMatrixQuestion(text, index, questionKind)
    case QuestionTypeCode.Ranked:
      return processRankedQuestion(text, index, questionKind)
    case QuestionTypeCode.Scale:
      return processScaleQuestion(text, index, questionKind)
    case QuestionTypeCode.FreeText:
      return processFreeTextQuestion(text, index, questionKind)
    case QuestionTypeCode.TextCard:
      return processTextCardQuestion(text, index, questionKind)
    case QuestionTypeCode.TextHighlighter:
      return processTextHighlighterQuestion(text, index, questionKind)
    case QuestionTypeCode.MaxDiff:
      return processMaxDiffQuestion(text, index, questionKind)
    case QuestionTypeCode.Conjoint:
      return processConjointQuestion(text, index, questionKind)
    default:
      return undefined
  }
}

const isQuestionLogicHeader = (text: string) => {
  return text.startsWith('Display this question ')
}

// `preserveEmptyLines` keeps blank paragraphs inside a question's group. A
// per-language export writes an untranslated field as an empty paragraph, and
// the grammar below is positional — dropping those blanks shifts every line
// after them, so the question text is read from the next field along. The
// creation path leaves it off: a master export has no blanks, and keeping them
// there would mint empty response options.
export const MARKET_EXPORT_IMPORT_ERROR =
  "This is a market-specific export and can't be imported. Use the Language-specific export to get an importable document."

export class MarketSpecificExportError extends Error {
  constructor() {
    super(MARKET_EXPORT_IMPORT_ERROR)
    this.name = 'MarketSpecificExportError'
  }
}

/**
 * Whether the doc is a market-specific export (its header carries
 * {@link MARKET_EXPORT_MARKER}). Those leave out what the market hides, so
 * their positions don't line up with the survey and must not be imported.
 */
export const isMarketSpecificExport = (doc: Document): boolean =>
  Array.from(doc.getElementsByTagName('w:p')).some(
    (p) => getTextFromParagraph(p).trim() === MARKET_EXPORT_MARKER
  )

export const getQuestionsFromDoc = (
  doc: Document,
  { includeTranslationOnlyTypes = false, preserveEmptyLines = false } = {}
) => {
  const paragraphs = doc.getElementsByTagName('w:p')
  let started = false
  let summaryStarted = false
  const result = Array.from(paragraphs).reduce((acc, p) => {
    const fullText = getTextFromParagraph(p)
    // These are the export's existing grammar markers, not question headers.
    // Checking them only inside `isNewQuestion` made the exclusion unreachable:
    // section titles and the trailing behaviour summary became answer options.
    // The summary is the final appendix; never resume parsing inside it.
    if (fullText.trim() === 'Question Behaviour Summary:') {
      summaryStarted = true
    }
    if (summaryStarted || isNotForImport(fullText.trim())) {
      started = false
      return acc
    }
    const isNewQuestion =
      isQuestionLogicHeader(fullText) || checkIfFullTextIsQuestion(fullText)
    if (isNewQuestion) {
      started = true
    }
    if (started) {
      if (isNewQuestion) {
        if (fullText !== '') acc.push([fullText])
      } else {
        const lastItem = acc[acc.length - 1]
        // @todo Legacy eslint violation – fix this when editing
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (lastItem && (fullText !== '' || preserveEmptyLines)) {
          lastItem.push(fullText)
          acc.splice(acc.length - 1, 1, lastItem)
        }
      }
    }
    return acc
  }, [] as string[][])
  // create questions
  const questions = result
    .filter((r) => r.some((text) => checkIfFullTextIsQuestion(text)))
    .map((r) => {
      const hasDisplayLogic = r.some((text) =>
        text.includes('Display this question IF')
      )
      const qInfo = hasDisplayLogic ? r[1] : r[0] // if has display logic, the question info is in the second item
      const qKind = qInfo.startsWith('A')
        ? QuestionKind.AudienceKind
        : QuestionKind.QuestionnaireKind

      const qTitleIndex = hasDisplayLogic ? 2 : 1
      const typeInfo = tryGetQuestionType(qInfo, includeTranslationOnlyTypes)
      // Conjoint and MaxDiff export with an empty type label, so their header
      // line ("Q5 ") passes the grouping test above while matching none of the
      // literals `tryGetQuestionType` knows. Destructuring the undefined it
      // returns used to throw, and the throw took the whole document down —
      // one conjoint question meant zero questions imported, and zero
      // translations applied. Skip the group instead; the caller reports the
      // unmatched positions.
      if (!typeInfo) return undefined
      const { questionType, settings, qNum } = typeInfo
      let question = processByQuestionType(
        r,
        qTitleIndex,
        questionType,
        qKind
      ) as RawQuestionnaireEntry
      question = {
        ...question,
        questionType,
        setting: settings,
        qNum
      }
      return question as RawQuestionnaireEntry
    })
    .filter((q): q is RawQuestionnaireEntry => q !== undefined)

  return questions
}
